import {
  Box,
  Button,
  chakra,
  Flex,
  Heading,
  Radio,
  RadioGroup,
  Stack,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import { EsgLabel, FormCheckBox, NumericFormInput } from "../../../../../../components/Forms";
import * as React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import {
  ELECTRICITY_TYPES,
  showErrorToast,
  showSuccessToast,
  translateSubElementString
} from "../../../../../../utils";
import {
  supplierQuestionnaireKeys,
  useDefaultSupplierQuestionnaireConfig,
  useFinalizeSupplierQuestionnaire
} from "../../../../../../hooks/query";
import { hideAppSpinner, showAppSpinner } from "../../../../../../context/Store/slices/appSpinnerSlice";
import { useDispatch } from "react-redux";
import { useCreateScopeTwo, useUpdateScopeTwo } from "../../../../../../hooks/query/supplier-questionnaire/tabs";
import {
  Answer
} from "../../../../../SupplierPhysicalRiskQuestionnaire/components/PhysicalRiskAssessmentForm/components";
import { ConfirmationDialog } from "../../../../../../components/ConfirmationDialog";
import { useQueryClient } from "react-query";
import { QuestionIconTooltip } from "../../../../../../components";

const scopeTwoAttributes = ["locationBasedEmissionCalculated", "locationBasedEmission", "marketBasedEmissionCalculated",
  "marketBasedEmission"];

function mapData(inputData) {
  const data = {};
  scopeTwoAttributes.forEach(attr => {
    data[attr] = inputData?.[attr] ?? "";
  });
  return data;
}

function ScopeTwo({
                    isTabActive, defaultValues, handleTabChange, isPreview,
                    returnToListRequested, declineReturnToListRequest, returnToList
                  }) {
  const { id: questionnaireId, emissionBased: { scopeTwo } } = defaultValues;
  const { t } = useTranslation("translation");
  const formDefaultValues = mapData(scopeTwo);
  const { isOpen: isConfirmDialogOpen, onOpen: onConfirmDialogOpen, onClose: onConfirmDialogClose } = useDisclosure();
  const [isDirty, setIsDirty] = useState(false);

  const {
    register,
    formState,
    control,
    reset,
    handleSubmit,
    watch,
    setValue
  } = useForm({
    defaultValues: formDefaultValues
  });

  const watchLocationBasedCheckbox = watch("locationBasedEmissionCalculated");
  const watchMarketBasedCheckbox = watch("marketBasedEmissionCalculated");

  useEffect(() => {
    if (watchLocationBasedCheckbox === false) {
      setValue("locationBasedEmission", "", { shouldDirty: true });
    }
  }, [watchLocationBasedCheckbox, setValue]);

  useEffect(() => {
    if (watchMarketBasedCheckbox === false) {
      setValue("marketBasedEmission", "", { shouldDirty: true });
    }
  }, [watchMarketBasedCheckbox, setValue]);

  const toast = useToast();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const [preferredApproach, setPreferredApproach] = useState(ELECTRICITY_TYPES.ELECTRICITY_LOCATION_BASED);
  const [isInitialState, setIsInitialState] = useState(true);
  const [direction, setDirection] = useState("");
  const [shouldChangeTab, setShouldChangeTab] = useState(false);
  const [finalizeQuestionnaire, setFinalizeQuestionnaire] = useState(false);

  useEffect(() => {
    if (formState.errors && Object.keys(formState.errors).length > 0) {
      if (finalizeQuestionnaire === true) {
        setFinalizeQuestionnaire(false);
      }
      if (returnToListRequested === true) {
        declineReturnToListRequest();
      }
    }
    if (formState.isDirty !== isDirty) {
      // Workaround that guarantees the actual value of isDirty attribute is used
      setIsDirty(formState.isDirty);
    }
  }, [formState, finalizeQuestionnaire, returnToListRequested, declineReturnToListRequest, isDirty]);

  useEffect(() => {
    if (isTabActive === true && returnToListRequested === true) {
      const scopeTwoForm = document.getElementById("supplier-questionnaire-scope-two");
      if (scopeTwoForm) {
        scopeTwoForm.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
      }
    }
  }, [isTabActive, returnToListRequested]);

  useEffect(() => {
    if (isInitialState && scopeTwo) {
      setPreferredApproach(scopeTwo.preferredApproach);
      setIsInitialState(false);
    }
  }, [isInitialState, scopeTwo]);

  const changeTab = () => {
    if (shouldChangeTab) {
      if (direction === "previous") {
        handleTabChange(prev => prev - 1);
        setDirection("");
        setShouldChangeTab(false);
      } else if (direction === "next") {
        handleTabChange(prev => prev + 1);
        setDirection("");
        setShouldChangeTab(false);
      }
    }
  };

  const queryConfig = {
    onSuccess: () => {
      if (finalizeQuestionnaire) {
        finalize(questionnaireId);
      } else {
        showSuccessToast(toast, t("entitySaved", { entityName: t("supplierQuestionnaire.scopeTwo.entityName") }));
        queryClient.invalidateQueries(supplierQuestionnaireKeys.supplierQuestionnaireById());
        if (returnToListRequested === true) {
          returnToList();
        }
        if (!isLoadingOnCreate && !isLoadingOnUpdate) {
          changeTab();
        }
      }
    },
    onError: err => showErrorToast(toast, err.message),
    onSettled: () => dispatch(hideAppSpinner())
  };

  const { mutate: create, isLoading: isLoadingOnCreate } = useCreateScopeTwo(queryConfig);
  const { mutate: update, isLoading: isLoadingOnUpdate } = useUpdateScopeTwo(queryConfig);
  const { mutate: finalize } = useFinalizeSupplierQuestionnaire({
    ...useDefaultSupplierQuestionnaireConfig(),
    onSuccess: () => {
      showSuccessToast(toast, t("supplierQuestionnaire.page.finalized"));
      returnToList();
    },
    onError: err => {
      showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);
      setFinalizeQuestionnaire(false);
    }
  });

  useEffect(() => {
    if (isLoadingOnCreate || isLoadingOnUpdate) {
      dispatch(showAppSpinner());
    }
  }, [dispatch, isLoadingOnCreate, isLoadingOnUpdate]);

  const onSubmit = (formData) => {
    const apiData = mapData(formData);
    apiData.preferredApproach = preferredApproach;
    if (isDirty || apiData.preferredApproach !== scopeTwo?.preferredApproach) {
      if (scopeTwo?.id) {
        update({ questionnaireId, id: scopeTwo.id, data: apiData });
      } else {
        create({ questionnaireId, data: apiData });
      }
      reset(formData);
    } else {
      return finalizeQuestionnaire ? finalize(questionnaireId) :
        returnToListRequested === true ? returnToList() :
          shouldChangeTab ? changeTab() : null;
    }
  };

  const handleFinalizeConfirmation = () => {
    handleSubmit(onSubmit)();
    handleDialogClose(true);
  };

  const handleDialogClose = (confirmed) => {
    if (confirmed !== true) {
      setFinalizeQuestionnaire(false);
    }
    onConfirmDialogClose();
  };

  const electricityTranslationKeys = {
    [ELECTRICITY_TYPES.ELECTRICITY_LOCATION_BASED]: "locationBased",
    [ELECTRICITY_TYPES.ELECTRICITY_MARKET_BASED]: "marketBased"
  };

  return (
    <chakra.form id="supplier-questionnaire-scope-two" onSubmit={handleSubmit(onSubmit)}>
      <Flex direction="column">
        <Box>
          {isPreview ?
            <Flex alignItems="center" whiteSpace="break-spaces">
              <QuestionIconTooltip
                label={t("supplierQuestionnaire.scopeTwo.locationBasedTooltip")}
                ariaLabel="location-based-tooltip"
              />
              <Heading color="esg_gray.200" size="xs">
                {t("supplierQuestionnaire.scopeTwo.locationBased")}
              </Heading>
              {` - `}
              <Answer
                answerText={formDefaultValues.locationBasedEmissionCalculated ?
                  t(`supplierQuestionnaire.buttons.yes`) : t(`supplierQuestionnaire.buttons.no`)}
              />
            </Flex> :
            <Flex alignItems="center">
              <FormCheckBox
                label={t("supplierQuestionnaire.scopeTwo.locationBased")}
                errors={formState.errors}
                {...register("locationBasedEmissionCalculated")}
                control={control}
                controlProps={{ maxInlineSize: "max-content" }}
              />
              <QuestionIconTooltip
                label={t("supplierQuestionnaire.scopeTwo.locationBasedTooltip")}
                ariaLabel="location-based-tooltip"
              />
            </Flex>
          }

          {watchLocationBasedCheckbox === true && isPreview &&
            <Box>
              <EsgLabel marginBlockStart={5}
                        text={translateSubElementString("supplierQuestionnaire.scopeTwo.emissions")} />
              <Answer answerText={formDefaultValues.locationBasedEmission?.toString()} />
            </Box>
          }

          {watchLocationBasedCheckbox === true && !isPreview &&
            <Flex alignItems="center" gap={10}>
              <EsgLabel marginBlockStart={5}
                        text={translateSubElementString("supplierQuestionnaire.scopeTwo.emissions")} />
              <NumericFormInput
                label={translateSubElementString("supplierQuestionnaire.scopeTwo.tCO2e")}
                errors={formState.errors}
                {...register("locationBasedEmission")}
                disabled={watchLocationBasedCheckbox === false}
              />
            </Flex>
          }
        </Box>

        <Box marginBlockStart={10}>
          {isPreview ?
            <Flex alignItems="center" whiteSpace="break-spaces">
              <QuestionIconTooltip
                label={t("supplierQuestionnaire.scopeTwo.marketBasedTooltip")}
                ariaLabel="market-based-tooltip"
              />
              <Heading color="esg_gray.200" size="xs">
                {t("supplierQuestionnaire.scopeTwo.marketBased")}
              </Heading>
              {` - `}
              <Answer
                answerText={formDefaultValues.marketBasedEmissionCalculated ?
                  t(`supplierQuestionnaire.buttons.yes`) : t(`supplierQuestionnaire.buttons.no`)}
              />
            </Flex> :
            <Flex alignItems="center">
              <FormCheckBox
                label={t("supplierQuestionnaire.scopeTwo.marketBased")}
                errors={formState.errors}
                {...register("marketBasedEmissionCalculated")}
                control={control}
                controlProps={{ maxInlineSize: "max-content" }}
              />
              <QuestionIconTooltip
                label={t("supplierQuestionnaire.scopeTwo.marketBasedTooltip")}
                ariaLabel="market-based-tooltip"
              />
            </Flex>
          }

          {watchMarketBasedCheckbox === true && isPreview &&
            <Box>
              <EsgLabel marginBlockStart={5}
                        text={translateSubElementString("supplierQuestionnaire.scopeTwo.emissions")} />
              <Answer answerText={formDefaultValues.marketBasedEmission?.toString()} />
            </Box>
          }

          {watchMarketBasedCheckbox === true && !isPreview &&
            <Flex alignItems="center" gap={10}>
              <EsgLabel marginBlockStart={5}
                        text={translateSubElementString("supplierQuestionnaire.scopeTwo.emissions")} />
              <NumericFormInput
                label={translateSubElementString("supplierQuestionnaire.scopeTwo.tCO2e")}
                errors={formState.errors}
                {...register("marketBasedEmission")}
                disabled={watchMarketBasedCheckbox === false}
              />
            </Flex>
          }
        </Box>

        <Box marginBlockStart={10}>
          <EsgLabel text={`${t("supplierQuestionnaire.scopeTwo.preferredApproach")}: *`} />
          {isPreview ?
            <Answer
              answerText={t(`supplierQuestionnaire.scopeTwo.${electricityTranslationKeys[preferredApproach]}`)}
            /> :
            <RadioGroup value={preferredApproach} style={{ position: "relative" }}>
              <Stack spacing={5} direction="row">
                <Radio
                  colorScheme="customScheme"
                  value={ELECTRICITY_TYPES.ELECTRICITY_LOCATION_BASED}
                  onChange={e => {
                    setPreferredApproach(e?.target.value);
                  }}
                >
                  {t("supplierQuestionnaire.scopeTwo.locationBased")}
                </Radio>
                <Radio
                  colorScheme="customScheme"
                  value={ELECTRICITY_TYPES.ELECTRICITY_MARKET_BASED}
                  onChange={e => {
                    setPreferredApproach(e?.target.value);
                  }}
                >
                  {t("supplierQuestionnaire.scopeTwo.marketBased")}
                </Radio>
              </Stack>
            </RadioGroup>
          }
        </Box>
      </Flex>

      {!isPreview ?
        <Flex
          justifyContent="space-between"
          marginBlockStart="10"
          position="sticky"
          bottom="0"
          backgroundColor="#fff"
          borderColor="esg_gray.100"
          bordersyle="solid"
          borderTopWidth="1px"
          paddingBlock="4"
          zIndex="10"
        >
          <Flex gap="2">
            <Button
              colorScheme="white"
              color="esg_lightgreen"
              border={"1px solid"}
              borderColor={"esg_lightgreen"}
              width="32"
              type="submit"
              onClick={() => {
                setDirection("previous");
                setShouldChangeTab(true);
              }}
            >
              {t("buttons.previous")}
            </Button>
            <Button
              colorScheme="white"
              color="esg_lightgreen"
              border={"1px solid"}
              borderColor={"esg_lightgreen"}
              type="submit"
              width="32"
              onClick={() => {
                setDirection("next");
                setShouldChangeTab(true);
              }}
            >
              {t("buttons.next")}
            </Button>
          </Flex>
          <Flex gap="2">
            <Button
              colorScheme="white"
              color="esg_lightgreen"
              border={"1px solid"}
              borderColor={"esg_lightgreen"}
              type="submit"
              width="32"
              onClick={() => setShouldChangeTab(false)}
            >
              {t("buttons.save")}
            </Button>
            <Button
              colorScheme="customScheme"
              width="32"
              onClick={() => {
                setFinalizeQuestionnaire(true);
                onConfirmDialogOpen();
              }}
            >
              {t("buttons.finalize")}
            </Button>
          </Flex>
        </Flex> : null
      }
      <ConfirmationDialog
        isOpen={isConfirmDialogOpen}
        onClose={handleDialogClose}
        onConfirm={handleFinalizeConfirmation}
        headerText={t("questionnaireFinalizationDialog.header")}
        confirmationText={t("questionnaireFinalizationDialog.question")}
      />
    </chakra.form>
  );
}

export default ScopeTwo;
